/**
 * Recording for the Lab's tenant speech recognition. The tenant's recogniser
 * accepts only WAV or Ogg, while MediaRecorder gives WebM (Chrome) or MP4
 * (Safari). So the capture API is MediaRecorder, and the result is always
 * decoded and re-encoded as WAV, 16-bit PCM, mono, 16 kHz.
 *
 * Every function takes the window so a test can stub it. Audio only passes
 * through memory: nothing is stored or logged.
 */

export const STT_SAMPLE_RATE = 16_000;
/** The tenant's upload cap; 2 MiB of this WAV is about 65 s. */
export const STT_MAX_BYTES = 2 * 1024 * 1024;
/** A recording stops by itself at this length. */
export const MAX_RECORD_MS = 60_000;

/** A mono Float32 signal (-1..1) as a 44-byte-header RIFF/WAV, PCM16 little-endian. */
export function encodeWav16kMono(samples: Float32Array): Blob {
  const dataBytes = samples.length * 2;
  const view = new DataView(new ArrayBuffer(44 + dataBytes));
  const text = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };
  text(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, STT_SAMPLE_RATE, true);
  view.setUint32(28, STT_SAMPLE_RATE * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  text(36, "data");
  view.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i += 1) {
    const clipped = Math.max(-1, Math.min(1, samples[i]!));
    view.setInt16(
      44 + i * 2,
      Math.round(clipped * (clipped < 0 ? 0x8000 : 0x7fff)),
      true,
    );
  }
  return new Blob([view.buffer], { type: "audio/wav" });
}

export interface RecorderLike {
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: (() => void) | null;
  mimeType?: string;
  start(): void;
  stop(): void;
}
type TrackLike = { stop(): void };
type StreamLike = { getTracks(): TrackLike[] };
type AudioBufferLike = {
  readonly duration: number;
  readonly numberOfChannels: number;
};
type ContextLike = {
  decodeAudioData(data: ArrayBuffer): Promise<AudioBufferLike>;
  close?(): Promise<void>;
};
type OfflineLike = {
  destination: unknown;
  createBufferSource(): {
    buffer: unknown;
    connect(node: unknown): void;
    start(): void;
  };
  startRendering(): Promise<{ getChannelData(channel: number): Float32Array }>;
};

type RecorderWindow = {
  navigator?: {
    mediaDevices?: { getUserMedia?(constraints: unknown): Promise<unknown> };
  };
  MediaRecorder?: new (stream: never) => RecorderLike;
  AudioContext?: new () => ContextLike;
  OfflineAudioContext?: new (
    channels: number,
    length: number,
    sampleRate: number,
  ) => OfflineLike;
};

function recorderWindow(win?: unknown): RecorderWindow | null {
  const candidate = win ?? (typeof window === "undefined" ? undefined : window);
  return candidate === undefined || candidate === null
    ? null
    : (candidate as RecorderWindow);
}

/** Whether this browser can record and decode audio. */
export function canRecord(win?: unknown): boolean {
  const w = recorderWindow(win);
  return (
    w?.navigator?.mediaDevices?.getUserMedia !== undefined &&
    w.MediaRecorder !== undefined &&
    w.AudioContext !== undefined &&
    w.OfflineAudioContext !== undefined
  );
}

export interface Recording {
  /** Asks the recorder to stop; `done` then settles. Safe to call twice. */
  stop(): void;
  /** Stops and drops the audio. */
  cancel(): void;
  /** The recorded audio in the browser's own format, once stopped. */
  done: Promise<Blob>;
}

/**
 * Starts recording the microphone. Rejects with the getUserMedia error (a
 * permission denial is a `NotAllowedError`). Stops itself after `maxMs`. The
 * microphone is released as soon as the recording stops.
 */
export async function startRecording(
  win?: unknown,
  maxMs: number = MAX_RECORD_MS,
): Promise<Recording> {
  const w = recorderWindow(win);
  const getUserMedia = w?.navigator?.mediaDevices?.getUserMedia;
  if (!w || getUserMedia === undefined || w.MediaRecorder === undefined) {
    throw new Error("recording unsupported");
  }
  const stream = (await getUserMedia.call(w.navigator!.mediaDevices, {
    audio: true,
  })) as StreamLike;
  const release = () => stream.getTracks().forEach((track) => track.stop());
  let recorder: RecorderLike;
  try {
    recorder = new w.MediaRecorder(stream as never);
  } catch (error) {
    release();
    throw error;
  }
  const stop = () => {
    try {
      recorder.stop();
    } catch {
      // Already stopped.
    }
  };
  const timer = setTimeout(stop, maxMs);
  const chunks: Blob[] = [];
  let dropped = false;
  const done = new Promise<Blob>((resolve, reject) => {
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    recorder.onstop = () => {
      clearTimeout(timer);
      release();
      if (dropped) {
        reject(new Error("recording cancelled"));
      } else {
        resolve(new Blob(chunks, { type: recorder.mimeType ?? "" }));
      }
    };
    recorder.onerror = () => {
      clearTimeout(timer);
      release();
      reject(new Error("recording failed"));
    };
  });
  try {
    recorder.start();
  } catch (error) {
    clearTimeout(timer);
    release();
    throw error;
  }
  return {
    stop,
    cancel() {
      dropped = true;
      done.catch(() => undefined);
      stop();
      release();
    },
    done,
  };
}

/** Any browser audio blob as a 16 kHz mono WAV. Rejects when it cannot be decoded. */
export async function toWav16kMono(blob: Blob, win?: unknown): Promise<Blob> {
  const w = recorderWindow(win);
  if (w?.AudioContext === undefined || w.OfflineAudioContext === undefined) {
    throw new Error("audio decoding unsupported");
  }
  const context = new w.AudioContext();
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    const length = Math.max(1, Math.ceil(decoded.duration * STT_SAMPLE_RATE));
    // Rendering into one mono channel at 16 kHz mixes down and resamples.
    const offline = new w.OfflineAudioContext(1, length, STT_SAMPLE_RATE);
    const source = offline.createBufferSource();
    source.buffer = decoded;
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    return encodeWav16kMono(rendered.getChannelData(0));
  } finally {
    void context.close?.().catch(() => undefined);
  }
}
